import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

// Side-effect: register direct-apply hooks for all integrations
import "../hooks";
import "../api";

import { useDirectApplyDataProvider } from "../providers/useDirectApplyDataProvider";

import ModernPageLayout from "@/components/layout/ModernPageLayout";
import { ROUTES } from "@/constants/routes";
import { useIntegrationModule } from "@/features/integrations/hooks/useIntegrationModule";
import { useIntegrationModuleClientDetailsBreadcrumbs } from "@/features/integrations/hooks/useIntegrationModuleClientDetailsBreadcrumbs";
import { ModuleDetailPageEmptyState } from "@/features/integrations/shared/components/ModuleDetailPageEmptyState";
import {
  getActionHandlers,
  renderComponent,
} from "@/features/integrations/shared/renderer";
import { useSchemaIntegrationData } from "@/features/integrations/shared/renderer/hooks/useSchemaIntegrationData";
import { decodeSlug } from "@/features/integrations/utils/slug";
import { useAuth } from "@/lib/hooks/useAuth";
import { useSearchSearchParams } from "@/lib/hooks/useSearchSearchParams";
import {
  useSortSearchParams,
  useTableSortHandler,
} from "@/lib/hooks/useSortSearchParams";

function DirectApplyClientDetailPage() {
  const { t } = useTranslation();
  const { tenantId } = useAuth();
  const {
    clientId,
    integrationName: rawIntegrationName,
    moduleName: moduleNameParam,
  } = useParams<{
    clientId?: string;
    integrationName?: string;
    moduleName?: string;
  }>();

  const decodedIntegrationName = rawIntegrationName
    ? decodeSlug(rawIntegrationName)
    : null;
  const backendIntegrationName = decodedIntegrationName ?? "";
  const effectiveModuleName = moduleNameParam || "direct-apply";

  const { breadcrumbs, integration } =
    useIntegrationModuleClientDetailsBreadcrumbs("direct-apply");

  const [searchParams, setSearchParams] = useSearchParams();
  const { searchValue, setSearchValue } = useSearchSearchParams();
  const { orderBy, descending } = useSortSearchParams();
  const handleSort = useTableSortHandler();

  const filterValues = useMemo(() => {
    const status = searchParams.get("status") || "all";
    return { syncStatus: status };
  }, [searchParams]);

  const handleFilterChange = useCallback(
    (key: string, value: unknown) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (key === "syncStatus") {
            next.set("status", String(value ?? "all"));
          }
          next.set("page", "1");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const moduleQuery = useIntegrationModule({
    integrationName: backendIntegrationName,
    moduleName: effectiveModuleName,
    tenantId: tenantId ?? undefined,
    clientId: clientId ?? undefined,
  });

  const isModuleActive = moduleQuery.data?.isActive ?? false;
  const provider = useDirectApplyDataProvider(decodedIntegrationName ?? undefined, clientId);
  const ui = useMemo(() => provider.getClientViews(), [provider]);

  const integrationData = useSchemaIntegrationData(
    backendIntegrationName,
    "direct-apply",
    clientId,
    tenantId ?? undefined,
  );

  const actionHandlers = useMemo(
    () => getActionHandlers(backendIntegrationName),
    [backendIntegrationName],
  );

  const executeAction = useCallback(
    async (actionKey: string) => {
      if (actionKey === "retry") {
        integrationData.refetch();
        return;
      }
      const handler = actionHandlers[actionKey];
      if (handler) {
        await handler({
          integrationName: backendIntegrationName,
          moduleName: effectiveModuleName,
          tenantId: tenantId ?? undefined,
          clientId: clientId ?? undefined,
        });
      }
    },
    [actionHandlers, backendIntegrationName, effectiveModuleName, tenantId, clientId, integrationData],
  );

  const handleSortWrapper = useCallback(
    (accessor: string | undefined, direction: string | null) => {
      if (!accessor) return;
      handleSort(accessor, direction as "asc" | "desc" | null);
    },
    [handleSort],
  );

  const context = useMemo(
    () => ({
      integrationName: backendIntegrationName,
      moduleName: effectiveModuleName,
      tenantId: tenantId ?? undefined,
      clientId: clientId ?? undefined,
      executeAction,
      data: integrationData.data,
      isLoading: integrationData.isLoading,
      onSort: handleSortWrapper,
      orderBy: orderBy ?? null,
      descending: descending ?? false,
      searchValue: searchValue ?? "",
      onSearchChange: setSearchValue,
      filterValues,
      onFilterChange: handleFilterChange,
      emptyMessage: t("common.noResultsFound"),
    }),
    [
      backendIntegrationName,
      effectiveModuleName,
      tenantId,
      clientId,
      executeAction,
      integrationData.data,
      integrationData.isLoading,
      handleSortWrapper,
      orderBy,
      descending,
      searchValue,
      setSearchValue,
      filterValues,
      handleFilterChange,
      t,
    ],
  );

  if (moduleQuery.isPending) return null;
  if (moduleQuery.isError || !moduleQuery.data || !isModuleActive) return null;

  const translatedIntegrationName = integration?.displayName
    ? t(integration.displayName)
    : integration?.name || backendIntegrationName || "";

  const backHref =
    clientId && rawIntegrationName
      ? `/${ROUTES.clients}/${clientId}/${ROUTES.integrations}/${rawIntegrationName}`
      : rawIntegrationName
        ? `/${ROUTES.integrations}/${rawIntegrationName}`
        : undefined;

  return (
    <ModernPageLayout
      breadcrumbs={breadcrumbs}
      title={t("integrations.modules.names.directApply", {
        defaultValue: "Direct Apply",
      })}
      subtitle={t(`integrations.${backendIntegrationName.toLowerCase()}.directApply.subtitle`, {
        defaultValue: t("integrations.modules.descriptions.directApply", { defaultValue: "" }),
      })}
      backbutton={backHref ? { label: translatedIntegrationName, href: backHref } : undefined}
    >
      {ui.length > 0 ? (
        <div className="space-y-6">
          {ui.map((component, index) => (
            <div key={`${component.component}-${index}`}>
              {renderComponent(component, context)}
            </div>
          ))}
        </div>
      ) : (
        <ModuleDetailPageEmptyState />
      )}
    </ModernPageLayout>
  );
}

export default DirectApplyClientDetailPage;
